import type { ApiResponse } from "../models/ApiResponse";
import type { ProcessResponse } from "../models/ProcessResponse";
import { getAxios } from "../helpers/api";
import { config } from '../environments/environment'

export interface LogTrackingInput {
    treeFellId: number;
    trackingDate: string;
    waybillNumber: string;
    dispatchedBy: string;
    truckNumber: string;
    client: string;
    clientId: number;
}

export interface ClientCompartment {
    compartmentId: number;
    compartmentName?: string;
    concessionName?: string;
}

export interface ClientTrackingSummary {
    clientId: number;
    client: string;
    trips: number;
    logs: number;
    totalVolume: number;
    totalCost: number;
    compartments: ClientCompartment[];
}

export interface LogTracking extends LogTrackingInput {
    logTrackingId: number;
    treeFellCode?: string;
    dateCreated: string;
}

class _LogTrackingService {
    private baseUrl = config.url.STOCK_API_URL;

    async AddLogTracking(input: LogTrackingInput): Promise<ProcessResponse> {
        try {
            const res = await getAxios(this.baseUrl).post("/Log/add/tracking", input);
            const data = res.data as ApiResponse<any>;
            if (data.isSuccessful) return { Successful: true, message: data.message || "Log tracking saved", data: data.data };

            return { Successful: false, message: data.message ?? "Unable to save log tracking. Please try again" }
        } catch (error) {
            return { Successful: false, message: "An error occurred. Please try again later" };
        }
    }

    async UpdateLogTracking(logTrackingId: number, input: LogTrackingInput): Promise<ProcessResponse> {
        try {
            const res = await getAxios(this.baseUrl).post("/Log/update/tracking", { logTrackingId, ...input });
            const data = res.data as ApiResponse<any>;
            if (data.isSuccessful) return { Successful: true, message: data.message || "Log tracking updated" };

            return { Successful: false, message: data.message ?? "Unable to update log tracking. Please try again" }
        } catch (error) {
            return { Successful: false, message: "An error occurred. Please try again later" };
        }
    }

    async GetAllLogTrackings(): Promise<LogTracking[] | null> {
        try {
            const res = await getAxios(this.baseUrl).get("/Log/get/trackings");
            const response = res.data as ApiResponse<LogTracking[]>;
            if (response.isSuccessful) {
                return [...(response.data ?? [])].sort((a, b) =>
                    (b.trackingDate ?? "").localeCompare(a.trackingDate ?? "") ||
                    (b.dateCreated ?? "").localeCompare(a.dateCreated ?? "")
                );
            }
            return [];
        } catch (error) {
            return null;
        }
    }

    async GetLogTracking(logTrackingId: number): Promise<LogTracking | null> {
        try {
            const res = await getAxios(this.baseUrl).get(`/Log/get/tracking/by/id/${logTrackingId}`);
            const response = res.data as ApiResponse<LogTracking>;
            if (response.isSuccessful) return response.data;
            return null;
        } catch (error) {
            return null;
        }
    }

    async GetClientSummary(clientId: number): Promise<ClientTrackingSummary | null> {
        try {
            const res = await getAxios(this.baseUrl).get(`/Log/get/tracking/summary/by/client/${clientId}`);
            const response = res.data as ApiResponse<ClientTrackingSummary>;
            return response.isSuccessful && response.data ? response.data : null;
        } catch (error) {
            return null;
        }
    }
}

export const LogTrackingService = new _LogTrackingService();
